import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useEditorStore } from '@/stores/editorStore';
import { api, ApiError } from '@/api/client';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { EditorTopBar } from '@/components/editor/EditorTopBar';
import { EditorToolbar } from '@/components/editor/EditorToolbar';
import { EditorCanvas, EditorCanvasRef } from '@/components/editor/EditorCanvas';
import { EditorPropertiesPanel } from '@/components/editor/EditorPropertiesPanel';
import { PageNavigator } from '@/components/editor/PageNavigator';
import { useEditorKeyboard } from '@/hooks/useEditorKeyboard';
import type { Tool } from '@/types/editor';
import type { Scrapbook, Page, PageSummary } from '@shared/types';
import './EditorPage.css';

export function EditorPage() {
  const { scrapbookId } = useParams<{ scrapbookId: string }>();
  const navigate = useNavigate();

  // Global editor store
  const {
    scrapbook,
    pages,
    currentPageId,
    hasUnsavedChanges,
    isSaving,
    setScrapbook,
    setPages,
    setCurrentPage,
    markUnsaved,
    markSaved,
    setSaving,
  } = useEditorStore();

  // Local state
  const [activeTool, setActiveTool] = useState<Tool>('select');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);

  // Ref for EditorCanvas
  const canvasRef = useRef<EditorCanvasRef>(null);

  // Keyboard shortcuts
  useEditorKeyboard(setActiveTool, canvasRef as React.MutableRefObject<unknown>);

  // Load scrapbook and pages on mount
  useEffect(() => {
    if (!scrapbookId) {
      setError('No scrapbook ID provided');
      setIsLoading(false);
      return;
    }

    loadScrapbook();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrapbookId]);

  const loadScrapbook = async () => {
    if (!scrapbookId) return;

    setIsLoading(true);
    setError(null);

    try {
      // Load scrapbook and pages in parallel
      const [scrapbookRes, pagesRes] = await Promise.all([
        api.get<Scrapbook>(`/scrapbooks/${scrapbookId}`),
        api.get<{ pages: PageSummary[] }>(`/scrapbooks/${scrapbookId}/pages`),
      ]);

      setScrapbook(scrapbookRes);

      // If there are pages, load the first page's full content
      if (pagesRes.pages.length > 0) {
        const firstPageId = pagesRes.pages[0].id;
        const fullPage = await api.get<Page>(`/pages/${firstPageId}`);
        setPages([fullPage]);
        setCurrentPage(firstPageId);
      } else {
        setPages([]);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 404) {
          setError('Scrapbook not found');
        } else if (err.status === 403) {
          setError('You do not have permission to view this scrapbook');
        } else {
          setError('Failed to load scrapbook');
        }
      } else {
        setError('Failed to load scrapbook');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-save with debounce
  useEffect(() => {
    if (!hasUnsavedChanges || !currentPageId) return;

    const timer = setTimeout(async () => {
      await savePage();
    }, 2000);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUnsavedChanges, currentPageId]);

  const savePage = useCallback(async () => {
    if (!currentPageId || !canvasRef.current) return;

    setSaving(true);
    try {
      // Get SVG content from canvas
      const svgContent = canvasRef.current.toJSON();

      await api.put(`/pages/${currentPageId}`, { svgContent });
      markSaved();
    } catch (err) {
      console.error('Failed to save page:', err);
      setActionError('Failed to save. Your changes may not be saved.');
      // Keep unsaved state so user knows save failed
    } finally {
      setSaving(false);
    }
  }, [currentPageId, setSaving, markSaved]);

  // Handle page switch
  const handlePageSelect = useCallback(async (pageId: string) => {
    // Save current page first if there are unsaved changes
    if (hasUnsavedChanges && currentPageId) {
      await savePage();
    }

    // Load the new page's content
    try {
      const fullPage = await api.get<Page>(`/pages/${pageId}`);
      // Update pages array with the new page content
      setPages(pages.map(p => p.id === pageId ? fullPage : p));
      setCurrentPage(pageId);
    } catch (err) {
      console.error('Failed to load page:', err);
      setActionError('Failed to load page. Please try again.');
    }
  }, [hasUnsavedChanges, currentPageId, savePage, pages, setPages, setCurrentPage]);

  // Handle add page
  const handleAddPage = useCallback(async () => {
    if (!scrapbookId) return;

    try {
      const newPage = await api.post<Page>(`/scrapbooks/${scrapbookId}/pages`, {
        pageOrder: pages.length,
        svgContent: '',
      });
      setPages([...pages, newPage]);
      setCurrentPage(newPage.id);
    } catch (err) {
      console.error('Failed to add page:', err);
      setActionError('Failed to add page. Please try again.');
    }
  }, [scrapbookId, pages, setPages, setCurrentPage]);

  // Handle delete page
  const handleDeletePage = useCallback(async (pageId: string) => {
    try {
      await api.delete(`/pages/${pageId}`);
      const newPages = pages.filter(p => p.id !== pageId);
      setPages(newPages);

      // If we deleted the current page, switch to another
      if (currentPageId === pageId && newPages.length > 0) {
        setCurrentPage(newPages[0].id);
      }
    } catch (err) {
      console.error('Failed to delete page:', err);
      setActionError('Failed to delete page. Please try again.');
    }
  }, [pages, currentPageId, setPages, setCurrentPage]);

  // Handle reorder pages
  const handleReorderPages = useCallback(async (pageIds: string[]) => {
    if (!scrapbookId) return;

    try {
      await api.patch(`/scrapbooks/${scrapbookId}/pages/reorder`, { pageIds });
      // Reorder local pages array to match
      const reorderedPages = pageIds
        .map(id => pages.find(p => p.id === id))
        .filter((p): p is Page => p !== undefined);
      setPages(reorderedPages);
    } catch (err) {
      console.error('Failed to reorder pages:', err);
      setActionError('Failed to reorder pages. Please try again.');
    }
  }, [scrapbookId, pages, setPages]);

  // Handle content change from canvas
  const handleContentChange = useCallback(() => {
    markUnsaved();
  }, [markUnsaved]);

  // Warn on navigation with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Loading state
  if (isLoading) {
    return (
      <div className="editor-loading">
        <LoadingSpinner size="lg" />
        <p>Loading editor...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="editor-error">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <h2>Unable to load editor</h2>
        <p>{error}</p>
        <div className="editor-error-actions">
          <button className="btn btn-secondary" onClick={() => navigate('/dashboard')}>
            Return to Dashboard
          </button>
          <button className="btn btn-primary" onClick={loadScrapbook}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!scrapbook) {
    return null;
  }

  const currentPage = pages.find(p => p.id === currentPageId);

  return (
    <div className="editor">
      <EditorTopBar
        scrapbook={scrapbook}
        isSaving={isSaving}
        hasUnsavedChanges={hasUnsavedChanges}
        onSave={savePage}
      />

      {actionError && (
        <div className="editor-notification editor-notification-error" role="alert">
          <span>{actionError}</span>
          <button
            className="editor-notification-dismiss"
            onClick={() => setActionError(null)}
            aria-label="Dismiss"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      )}

      <div className="editor-main">
        <EditorToolbar
          activeTool={activeTool}
          onToolChange={setActiveTool}
        />

        <div className="editor-canvas-area">
          <EditorCanvas
            ref={canvasRef}
            page={currentPage}
            scrapbook={scrapbook}
            activeTool={activeTool}
            zoom={zoom}
            onZoomChange={setZoom}
            onContentChange={handleContentChange}
          />
        </div>

        <EditorPropertiesPanel
          getElement={(id) => canvasRef.current?.getElement(id)}
          updateElement={(id, updates) => {
            canvasRef.current?.updateElement(id, updates);
            handleContentChange();
          }}
        />
      </div>

      <PageNavigator
        pages={pages}
        currentPageId={currentPageId}
        onPageSelect={handlePageSelect}
        onAddPage={handleAddPage}
        onDeletePage={handleDeletePage}
        onReorderPages={handleReorderPages}
      />
    </div>
  );
}
